import { ASTEROID_IMAGE_COUNT, ASTEROID_ORIGIN_SYSTEM, ASTEROID_SPAWN_CHANCE, ASTEROID_SPAWN_POSITION_MAX, ASTEROID_SPAWN_SLOT_MINUTES, ASTEROID_STEP_MINUTES_MIN, ASTEROID_STEP_MINUTES_MAX, STAR_IMAGE_COUNT, SYSTEM_POSITION_MAX, SYSTEMS_PER_GALAXY, } from '@kaeliux/shared';
/** 32-bit avalanche hash → uniform float in [0, 1). Pure and stable. */
function hash01(seed) {
    let h = seed >>> 0;
    h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
    h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
}
/** Independent hash stream per (galaxy, system, spawn slot, salt). */
function slotHash(galaxy, system, slot, salt) {
    // Distinct prime strides keep neighbouring coordinates/slots from colliding.
    return hash01(galaxy * 15485863 + system * 104729 + slot * 7919 + salt * 452930477);
}
const SLOT_MS = ASTEROID_SPAWN_SLOT_MINUTES * 60000;
const STEP_MIN_MS = ASTEROID_STEP_MINUTES_MIN * 60000;
const STEP_MAX_MS = ASTEROID_STEP_MINUTES_MAX * 60000;
/**
 * All asteroids currently in transit through `galaxy:system` at `now`,
 * sorted by position then id. Same inputs always produce the same output;
 * an asteroid's id and imageIndex never change between calls while it
 * lives.
 */
export function getSystemAsteroids(galaxy, system, now) {
    const nowMs = now.getTime();
    // Longest possible life: enter at position 1, slowest drift, one step
    // per position through 24 — no spawn slot older than that can still be
    // in the system, so the scan window is bounded.
    const maxLifetimeMs = SYSTEM_POSITION_MAX * STEP_MAX_MS;
    const firstSlot = Math.floor((nowMs - maxLifetimeMs) / SLOT_MS);
    const lastSlot = Math.floor(nowMs / SLOT_MS);
    const asteroids = [];
    for (let slot = firstSlot; slot <= lastSlot; slot++) {
        if (slotHash(galaxy, system, slot, 1) >= ASTEROID_SPAWN_CHANCE)
            continue;
        const spawnedAt = slot * SLOT_MS;
        if (spawnedAt > nowMs)
            continue;
        const spawnPosition = 1 + Math.floor(slotHash(galaxy, system, slot, 2) * ASTEROID_SPAWN_POSITION_MAX);
        const stepMs = Math.round(STEP_MIN_MS + slotHash(galaxy, system, slot, 3) * (STEP_MAX_MS - STEP_MIN_MS));
        const steps = Math.floor((nowMs - spawnedAt) / stepMs);
        const position = spawnPosition + steps;
        if (position > SYSTEM_POSITION_MAX)
            continue; // exited the system
        asteroids.push({
            id: `asteroid-${galaxy}-${system}-${slot}`,
            position,
            imageIndex: 1 + Math.floor(slotHash(galaxy, system, slot, 4) * ASTEROID_IMAGE_COUNT),
            spawnedAt,
            nextMoveAt: spawnedAt + (steps + 1) * stepMs,
        });
    }
    return asteroids.sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}
/**
 * `asteroid-{galaxy}-{originSystem}-{slot}` → `{ galaxy, originSystem, slot }`
 * — the inverse of the id format constructed in {@link getSystemAsteroids}
 * and {@link getDriftingSystemAsteroids}. Single source of truth so callers
 * that only have an asteroid's stable id (e.g. an `AsteroidCargo` doc) can
 * recover the triple {@link locateAsteroid} needs without duplicating the
 * split logic (and its exact field order) at every call site.
 */
export function parseAsteroidId(asteroidId) {
    const [, galaxyStr, originStr, slotStr] = asteroidId.split('-');
    return { galaxy: Number(galaxyStr), originSystem: Number(originStr), slot: Number(slotStr) };
}
/** One asteroid's spawn parameters — everything about it that's fixed for life, derived once from its (galaxy, origin system, slot). */
function asteroidSpawn(galaxy, originSystem, slot) {
    if (slotHash(galaxy, originSystem, slot, 1) >= ASTEROID_SPAWN_CHANCE)
        return null;
    return {
        spawnedAt: slot * SLOT_MS,
        spawnPosition: 1 + Math.floor(slotHash(galaxy, originSystem, slot, 2) * ASTEROID_SPAWN_POSITION_MAX),
        stepMs: Math.round(STEP_MIN_MS + slotHash(galaxy, originSystem, slot, 3) * (STEP_MAX_MS - STEP_MIN_MS)),
        imageIndex: 1 + Math.floor(slotHash(galaxy, originSystem, slot, 4) * ASTEROID_IMAGE_COUNT),
    };
}
/**
 * Locate ONE asteroid, identified by its stable (galaxy, origin system,
 * slot) — the same triple encoded in {@link SystemAsteroid.id} — without
 * scanning: its whole future position is a closed-form function of elapsed
 * time, so this is O(1) regardless of how many systems it has since crossed.
 * This is the building block `getDriftingSystemAsteroids` scans with below,
 * and also what the functions layer re-derives from an `AsteroidCargo`
 * doc's id alone to find out whether that asteroid has moved to a new
 * system or drifted out of the galaxy since it was last checked.
 */
export function locateAsteroid(galaxy, originSystem, slot, now) {
    const spawn = asteroidSpawn(galaxy, originSystem, slot);
    if (!spawn)
        return null;
    const nowMs = now.getTime();
    if (spawn.spawnedAt > nowMs)
        return null;
    const steps = Math.floor((nowMs - spawn.spawnedAt) / spawn.stepMs);
    // 0-indexed total positions advanced since originSystem's position 1 —
    // the same "spawn position + steps" math as getSystemAsteroids, just not
    // yet folded back into a single system's 1..24 range.
    const globalOffset = (spawn.spawnPosition - 1) + steps;
    const system = originSystem + Math.floor(globalOffset / SYSTEM_POSITION_MAX);
    if (system > SYSTEMS_PER_GALAXY)
        return null; // drifted past the last system — gone for good
    return {
        system,
        position: 1 + (globalOffset % SYSTEM_POSITION_MAX),
        imageIndex: spawn.imageIndex,
        spawnedAt: spawn.spawnedAt,
        nextMoveAt: spawn.spawnedAt + (steps + 1) * spawn.stepMs,
    };
}
/**
 * All asteroids currently in transit through `galaxy:system` at `now`,
 * sorted by position then id — the drifting model (#90 follow-up): unlike
 * {@link getSystemAsteroids}, `system` holds no stream of its own. Every
 * asteroid entered the galaxy at ASTEROID_ORIGIN_SYSTEM and drifted here,
 * so this scans that one origin's slot stream over a tightly-bounded window
 * — only spawn slots old enough to have possibly reached `system` by now,
 * but not so old they'd have already drifted past it.
 */
export function getDriftingSystemAsteroids(galaxy, system, now) {
    const nowMs = now.getTime();
    const asteroids = [];
    const systemsToTravel = system - ASTEROID_ORIGIN_SYSTEM;
    if (systemsToTravel < 0)
        return asteroids;
    // Earliest it could already be sitting in `system`: exactly
    // `systemsToTravel` full system-crossings at the FASTEST drift, minus up
    // to `ASTEROID_SPAWN_POSITION_MAX - 1` steps — an asteroid that entered
    // at position 2 or 3 (not just 1) needs that many fewer steps to cross
    // the same number of systems.
    const minSteps = Math.max(0, systemsToTravel * SYSTEM_POSITION_MAX - (ASTEROID_SPAWN_POSITION_MAX - 1));
    const minElapsedMs = minSteps * STEP_MIN_MS;
    // Latest it could still be sitting in `system` (not yet advanced into
    // `system + 1`): one more full system's worth of positions at the
    // SLOWEST drift.
    const maxElapsedMs = (systemsToTravel + 1) * SYSTEM_POSITION_MAX * STEP_MAX_MS;
    const firstSlot = Math.floor((nowMs - maxElapsedMs) / SLOT_MS);
    const lastSlot = Math.floor((nowMs - minElapsedMs) / SLOT_MS);
    for (let slot = firstSlot; slot <= lastSlot; slot++) {
        const located = locateAsteroid(galaxy, ASTEROID_ORIGIN_SYSTEM, slot, now);
        if (!located || located.system !== system)
            continue;
        asteroids.push({
            id: `asteroid-${galaxy}-${ASTEROID_ORIGIN_SYSTEM}-${slot}`,
            position: located.position,
            imageIndex: located.imageIndex,
            spawnedAt: located.spawnedAt,
            nextMoveAt: located.nextMoveAt,
        });
    }
    return asteroids.sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}
/**
 * A system's star art variant, 1..STAR_IMAGE_COUNT — a pure hash of its
 * coordinates so every viewer (and the server, if it ever needs to agree)
 * derives the same image without storing anything.
 */
export function getSystemStarImageIndex(galaxy, system) {
    return 1 + Math.floor(slotHash(galaxy, system, 0, 5) * STAR_IMAGE_COUNT);
}
//# sourceMappingURL=galaxy.js.map